import { FC, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Drawer } from '@new/common/ui/Drawer/Drawer';
import { Loader } from '@new/common/ui/Loader/Loader';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import {
  useAdminDeleteTranscriptMutation,
  useGetAdminEvaluationQuery,
  type AdminEvaluationSummary,
  type TranscriptBreakdown,
} from '@teachur/http-client';

import { EvaluationGeneralInfo } from './EvaluationGeneralInfo';
import { EvaluationTranscriptsList } from './EvaluationTranscriptsList';
import { TranscriptReviewPanel } from '../TranscriptReviewPanel/TranscriptReviewPanel';
import { AdminAddTranscriptModal } from '../AdminAddTranscriptModal/AdminAddTranscriptModal';

interface EvaluationReviewDrawerProps {
  evaluationId: number | null;
  placeholder?: AdminEvaluationSummary;
  onClose: () => void;
}

const DrawerHeader: FC<{
  name: string;
  email: string | null;
  onClose: () => void;
}> = ({ name, email, onClose }) => (
  <div className="bg-(--primary-O1000) px-4 py-4 text-(--neutral-white-NW1000)">
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <h4 className="m-0! [font-size:var(--fs-p2)]! font-semibold text-(--neutral-white-NW1000)!">
          Transcript review
        </h4>

        <button
          type="button"
          onClick={onClose}
          className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-(--neutral-white-NW1000)"
          aria-label="Close drawer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="min-w-0">
        <p className="m-0! [font-size:var(--fs-l4)]! font-semibold text-(--neutral-white-NW1000)! truncate">
          {name}
        </p>
        {email ? (
          <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-white-NW1000)! truncate">
            {email}
          </p>
        ) : null}
      </div>
    </div>
  </div>
);

export const EvaluationReviewDrawer: FC<EvaluationReviewDrawerProps> = ({
  evaluationId,
  placeholder,
  onClose,
}) => {
  const open = evaluationId !== null;

  const [selectedTranscriptId, setSelectedTranscriptId] = useState<number | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState<TranscriptBreakdown | null>(null);
  const deleteTranscript = useAdminDeleteTranscriptMutation();

  const onConfirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteTranscript.mutateAsync(toDelete.id);
      toast.success('Transcript deleted');
      if (selectedTranscriptId === toDelete.id) setSelectedTranscriptId(null);
      setToDelete(null);
    } catch {
      toast.error('Failed to delete transcript');
    }
  };

  const handleClose = () => {
    setSelectedTranscriptId(null);
    onClose();
  };

  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useGetAdminEvaluationQuery(evaluationId ?? 0, {
    enabled: open,
  });

  const selectedTranscript =
    selectedTranscriptId != null
      ? (detail?.transcripts.find((t) => t.id === selectedTranscriptId) ?? null)
      : null;

  const forbidden = isError && (error as { statusCode?: number })?.statusCode === 403;
  const notFound = isError && !forbidden;

  const userId = placeholder?.userId ?? detail?.userId ?? null;
  const studentName = placeholder?.studentName ?? (userId ? `User #${userId}` : 'Student');
  const studentEmail = placeholder?.studentEmail ?? null;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      className="max-w-130!"
      header={
        open ? (
          <DrawerHeader name={studentName} email={studentEmail} onClose={handleClose} />
        ) : undefined
      }
      isSidePanelOpen={selectedTranscript !== null}
      sidePanel={
        selectedTranscript ? (
          <TranscriptReviewPanel
            key={selectedTranscript.id}
            transcript={selectedTranscript}
            allTranscripts={detail?.transcripts ?? []}
            onOpenTranscript={setSelectedTranscriptId}
            onBack={() => setSelectedTranscriptId(null)}
          />
        ) : undefined
      }
    >
      <div className="flex flex-col gap-6 px-4 py-6 sm:px-6">
        {forbidden ? (
          <EmptyState
            message="You don't have access"
            description="You are not allowed to view this transcript evaluation."
          />
        ) : notFound ? (
          <EmptyState
            message="Evaluation not found"
            description="This transcript evaluation may have been removed."
          />
        ) : isLoading && !detail ? (
          <div className="flex items-center justify-center py-10">
            <Loader />
          </div>
        ) : detail ? (
          <>
            <EvaluationGeneralInfo evaluation={detail} />
            <EvaluationTranscriptsList
              transcripts={detail.transcripts}
              onSelect={setSelectedTranscriptId}
              onDelete={setToDelete}
              onAdd={() => setIsAddOpen(true)}
            />
          </>
        ) : null}
      </div>

      {userId != null ? (
        <AdminAddTranscriptModal
          key={userId}
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          lockedStudent={{
            id: userId,
            firstName: studentName,
            lastName: null,
            email: studentEmail,
          }}
        />
      ) : null}

      <ConfirmationModal
        isOpen={toDelete != null}
        title="Delete transcript?"
        description={`This will permanently delete the transcript${toDelete ? ` for ${toDelete.institution}` : ''} and its courses. This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteTranscript.isPending}
        onConfirm={onConfirmDelete}
        onClose={() => setToDelete(null)}
      />
    </Drawer>
  );
};
